import React, { useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import Dropdown from '../Dropdown';
import styles from './CountryDropdown.module.scss';
import { ReactComponent as DropdownIndicator } from '../../assets/images/icons/chevron_icon.svg';
import { isMobileDevice } from '../../lib/util/isMobileDevice';
import { useTranslation } from 'react-i18next';

interface CountryDropdownProps {
  country: string | undefined;
  onChange: (newCountry: string) => void;
  error?: boolean;
  disabled?: boolean;
  allowedCountries?: string[];
}

export default function CountryDropdown({
  country,
  onChange,
  error,
  disabled,
  allowedCountries,
}: CountryDropdownProps) {
  const { t } = useTranslation();
  const [isMobile, setIsMobile] = useState(isMobileDevice());

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(isMobileDevice());
    };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const allOptions = useMemo(
    () => [
      { value: 'CA', label: t('canada') },
      { value: 'FR', label: t('france') },
      { value: 'DE', label: t('germany') },
      { value: 'ES', label: t('spain') },
      { value: 'GB', label: t('united_kingdom') },
      { value: 'US', label: t('united_states') },
    ],
    [t],
  );

  const options = useMemo(
    () => (allowedCountries ? allOptions.filter(({ value }) => allowedCountries.includes(value)) : allOptions),
    [allOptions, allowedCountries],
  );

  const selectedCountry = useMemo(() => {
    if (!country) {
      return;
    }
    return options.find(({ value }) => value === country);
  }, [options, country]);

  if (disabled) {
    return <Dropdown id="country" selectedOption={selectedCountry} placeholder={t('country')} disabled={true} />;
  }

  if (isMobile) {
    return (
      <div className={styles.container}>
        <select
          id="country"
          className={classNames(styles.selectContainer, { [styles.valid]: country !== '' && !error })}
          value={country || ''}
          onChange={(e) => onChange(e.target.value)}
        >
          <option value="" disabled>
            {t('country')}
          </option>
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <DropdownIndicator />
      </div>
    );
  }

  return (
    <Dropdown
      id="country"
      options={options}
      selectedOption={selectedCountry}
      placeholder={t('country')}
      onChange={(option) => {
        if (option) {
          onChange((option as { value: string }).value);
        }
      }}
    />
  );
}
